import { useTheme } from "@mui/material";
import { SHStack, SHTypography } from "@components/design-systems";
import { SHTextOverflowTooltip } from "@components/design-systems/sh-text-overflow-tooltip";
import { NumericFormat } from "react-number-format";
import { BalanceAvailability } from "@models/platform-analysis/enums/fee/balanceAvailability";
import {
  getSetupRowErrorMessage,
  getSetupRowWarningMessage,
} from "../../utils";
import { AnalysisFeePortfolioWarningIcon } from "../../warning-icon";

type Props = {
  label: string;
  disabled?: boolean;
  code?: string;
  minimumInvestment?: number | null;
  isUnderMinimumInvestment?: boolean;
  isMissingInvestments?: boolean;
  isMissingInvestmentOption?: boolean;
  balanceAvailability?: BalanceAvailability;
  isUnavailable?: boolean;
  isIDPSBalance?: boolean;
  isSuperBalance?: boolean;
};

export const RowLabel = ({
  label,
  disabled,
  code,
  minimumInvestment,
  isUnderMinimumInvestment,
  isMissingInvestments,
  isMissingInvestmentOption,
  balanceAvailability,
  isUnavailable,
  isIDPSBalance,
  isSuperBalance,
}: Props) => {
  const { palette } = useTheme();

  const getWarningMessage = () => {
    if (isMissingInvestments) return null;
    const availability = balanceAvailability as BalanceAvailability;
    const displayData = getSetupRowWarningMessage({
      balanceAvailability: availability,
    });

    if (!displayData) return null;

    return (
      <AnalysisFeePortfolioWarningIcon
        type={"warning"}
        warningData={displayData}
      />
    );
  };

  const getErrorMessage = () => {
    const availability = balanceAvailability as BalanceAvailability;
    const errorData = getSetupRowErrorMessage({
      isMissingInvestments,
      isMissingInvestmentOption,
      balanceAvailability: availability,
      isUnavailable,
      isIDPSBalance,
      isSuperBalance,
    });

    if (!errorData) return null;

    return (
      <AnalysisFeePortfolioWarningIcon type={"error"} warningData={errorData} />
    );
  };

  const renderWaring = (): JSX.Element | null => {
    return (
      <SHStack direction={"row"} gap={0.5} paddingBottom={"2px"}>
        {getErrorMessage()}
        {getWarningMessage()}
      </SHStack>
    );
  };

  return (
    <SHStack height={"100%"} width={"100%"} gap={"1px"}>
      <SHStack
        direction={"row"}
        width={"100%"}
        maxWidth={450}
        gap={1}
        alignItems={"center"}
      >
        <SHTextOverflowTooltip tooltipContent={label} lineClamp={1}>
          <SHTypography
            disabled={disabled}
            variant={"subtitle1"}
            fontSize={13}
            color={palette.text.primary}
            fontWeight={700}
            lineHeight={1.2}
          >
            {label}
          </SHTypography>
        </SHTextOverflowTooltip>

        {renderWaring()}
      </SHStack>

      <SHStack direction={"row"} gap={0.5}>
        {code && (
          <SHTypography
            variant={"body2"}
            fontSize={13}
            color={palette.text.third}
            addPaddingTop={false}
            lineHeight={1.2}
          >
            {code} {minimumInvestment && " | "}
          </SHTypography>
        )}
        {minimumInvestment && (
          <SHTypography
            variant={"body2"}
            fontSize={13}
            addPaddingTop={false}
            lineHeight={1.2}
            color={
              isUnderMinimumInvestment
                ? palette.warning.main
                : palette.text.third
            }
          >
            Minimum investment:{" "}
            <NumericFormat
              displayType="text"
              defaultValue={0}
              value={minimumInvestment}
              thousandSeparator=","
              prefix={"$"}
              decimalScale={2}
            />
          </SHTypography>
        )}
      </SHStack>
    </SHStack>
  );
};
